import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Alert,
  Divider,
  CircularProgress,
  Breadcrumbs,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  IconButton,
  Paper,
  Chip,
  Stack,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import acProcessService from '../../../services/acProcessService';
import s3Utils from '../../../utils/s3Utils';

const AgendaDate = () => {
  const navigate = useNavigate();

  // State for search
  const [username, setUsername] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('0');
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // State for form data
  const [formData, setFormData] = useState({
    labName: '',
    labId: '',
    dealingOfficer: '',
    acType: '0',
    fromDate: '0',
    toDate: '0',
    certificateNo: '',
    issueDate: '',
    validityTill: '',
    amendmentDate: '',
    coolingOffApplicable: '',
    coolingOffDate: '',
    iacFile: null,
    coolingOffFile: null,
  });

  // State for data
  const [acDates, setAcDates] = useState([]);
  const [acDatesTo, setAcDatesTo] = useState([]);
  const [stage, setStage] = useState('');
  const [recordId, setRecordId] = useState(null);
  const [acStatus, setAcStatus] = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [showAcDetails, setShowAcDetails] = useState(false);
  const [showRollback, setShowRollback] = useState(false);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // File URLs and Names
  const [iacFileUrl, setIacFileUrl] = useState('');
  const [coolingOffFileUrl, setCoolingOffFileUrl] = useState('');
  const [iacFileName, setIacFileName] = useState('');
  const [coolingOffFileName, setCoolingOffFileName] = useState('');

  const handleSearch = async () => {
    if (!username.trim()) {
      showSnackbar('Please enter username', 'error');
      return;
    }

    try {
      setLoading(true);
      // Reset form data
      resetForm();
      setShowDetails(false);
      
      // Use sp_Common Mode 58 to get applications list (AppId, Name)
      const response = await acProcessService.getApplicationsByUsername(username);
      const apps = Array.isArray(response) ? response : (response.data || []);
      
      if (apps.length > 0) {
        setApplications(apps);
        setSelectedAppId('0');
      } else {
        setApplications([]);
        showSnackbar('No applications found for this username', 'warning');
      }
    } catch (err) {
      showSnackbar('Failed to search applications', 'error');
      console.error(err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = async (appId) => {
    setSelectedAppId(appId);
    
    if (appId === '0') {
      resetForm();
      setShowDetails(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use Mode 142 to get application details by appId and username
      const response = await acProcessService.getApplicationByAppIdUsername(appId, username);
      const appDetails = Array.isArray(response) ? response[0] : (response.data?.[0] || response.data || response);
      
      setFormData({
        labName: appDetails.laboratoryName || '',
        labId: appDetails.applicationno || appDetails.applicationNo || '',
        dealingOfficer: appDetails.name || appDetails.doName || '',
        acType: '0',
        fromDate: '0',
        toDate: '0',
        certificateNo: '',
        issueDate: '',
        validityTill: '',
        amendmentDate: '',
        coolingOffApplicable: '',
        coolingOffDate: '',
        iacFile: null,
        coolingOffFile: null,
      });
      setSelectedOrgId(appDetails.OrgId || '');
      setStage(appDetails.stage || '');
      
      // Check if AC Process updates should be shown
      const completedStages = ['7', '7a', '7b', '7c', '7d', '7e', '7f', '7g', '12', '12a', '12b', '13', '13a', '13d', '13e'];
      if (completedStages.includes(appDetails.stage)) {
        setShowAcDetails(true);
        setShowRollback(true);
      } else {
        setShowAcDetails(false);
        setShowRollback(false);
      }
      await loadExistingACData(appId, appDetails.OrgId);
      // Show the details section after loading application
      setShowDetails(true);
    } catch (err) {
      showSnackbar('Failed to load application details', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingACData = async (appId, orgId) => {
    try {
      // Use getACProcessDetails API to fetch AC data
      const response = await acProcessService.getACProcessDetails(appId, orgId);
      const acDataList = Array.isArray(response) ? response : (response.data || []);
      
      // Get the most recent/first record
      const acData = acDataList.length > 0 ? acDataList[0] : null;
      
      if (acData) {
        setRecordId(acData.Id);
        
        // Store AC Status for adverse decision check
        setAcStatus(acData.AC_Status || '');
        
        // First load the AC dates for the AC Type
        if (acData.AC_Type) {
          await loadACDatesForEdit(acData.AC_Type, acData.dateid, acData.dateidTo);
        }
        
        setFormData(prev => ({
          ...prev,
          acType: acData.AC_Type || '0',
          fromDate: acData.dateid?.toString() || '0',
          toDate: acData.dateidTo?.toString() || '0',
          certificateNo: acData.Cert_No || '',
          issueDate: acData.Issue_Date || '',
          validityTill: acData.ToDate || '',
          amendmentDate: acData.AmendmentDate || '',
          coolingOffApplicable: acData.IsCollingOffPeriod || '',
          coolingOffDate: acData.CollingOffPeriodDate || '',
        }));

        if (acData.IAC_Minutes) {
          // If IAC_Minutes contains full URL, extract file name
          const iacName = acData.IAC_Minutes.includes('http') 
            ? acData.IAC_Minutes.split('/').pop() 
            : acData.IAC_Minutes;
          setIacFileName(iacName);
          setIacFileUrl(acData.IAC_Minutes);
        }

        if (acData.CollingOfFile) {
          // If CollingOfFile contains full URL, extract file name
          const coolingName = acData.CollingOfFile.includes('http') 
            ? acData.CollingOfFile.split('/').pop() 
            : acData.CollingOfFile;
          setCoolingOffFileName(coolingName);
          setCoolingOffFileUrl(acData.CollingOfFile);
        }
      }
    } catch (err) {
      console.error('Failed to load AC data:', err);
    }
  };

  const loadACDatesForEdit = async (acType, fromDateId, toDateId) => {
    try {
      // Load from dates
      const fromResponse = await acProcessService.getRecentACDates(acType);
      const fromDates = Array.isArray(fromResponse) ? fromResponse : (fromResponse.data || []);
      setAcDates(fromDates);

      // If fromDateId exists, load to dates
      if (fromDateId && fromDateId !== '0') {
        const selectedFromDate = fromDates.find(d => d.Id === fromDateId || d.Id.toString() === fromDateId.toString());
        if (selectedFromDate) {
          const toResponse = await acProcessService.getACDateToOptions(acType, selectedFromDate.Date);
          const toDates = Array.isArray(toResponse) ? toResponse : (toResponse.data || []);
          setAcDatesTo(toDates);
        }
      }
    } catch (err) {
      console.error('Failed to load AC dates for editing:', err);
    }
  };

  const handleACTypeChange = async (value) => {
    setFormData(prev => ({ ...prev, acType: value }));
    
    if (value !== '0') {
      try {
        setLoading(true);
        const response = await acProcessService.getRecentACDates(value);
        const dates = Array.isArray(response) ? response : (response.data || []);
        setAcDates(dates);
      } catch (err) {
        showSnackbar('Failed to load AC dates', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFromDateChange = async (value) => {
    setFormData(prev => ({ ...prev, fromDate: value }));
    
    if (value !== '0') {
      try {
        setLoading(true);
        const selectedDate = acDates.find(d => d.Id === value);
        const response = await acProcessService.getACDateToOptions(
          formData.acType,
          selectedDate?.Date
        );
        const datesTo = Array.isArray(response) ? response : (response.data || []);
        setAcDatesTo(datesTo);
      } catch (err) {
        showSnackbar('Failed to load end dates', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileChange = async (field, file) => {
    if (!file) return;

    // Validate file type
    if (field === 'iacFile' && file.type !== 'application/pdf') {
      showSnackbar('Only PDF files are allowed for IAC minutes', 'error');
      return;
    }

    try {
      setLoading(true);
      showSnackbar('Uploading file to S3...', 'info');

      // Upload to S3
      const s3Url = await s3Utils.uploadFile(
        file,
        `ac-process/${selectedAppId}/${field}`,
        {
          onProgress: (progress) => {
            console.log(`Upload progress: ${progress}%`);
          }
        }
      );

      // Extract file name from S3 URL (last part after the last /)
      const fileName = s3Url.split('/').pop() || file.name;

      // Set the S3 URL and file name based on field
      if (field === 'iacFile') {
        setIacFileUrl(s3Url);
        setIacFileName(fileName);
      } else if (field === 'coolingOffFile') {
        setCoolingOffFileUrl(s3Url);
        setCoolingOffFileName(fileName);
      }

      // Clear the file input
      setFormData(prev => ({ ...prev, [field]: null }));
      
      showSnackbar('File uploaded successfully', 'success');
    } catch (err) {
      console.error('File upload error:', err);
      showSnackbar('Failed to upload file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (field) => {
    if (field === 'iacFile') {
      setIacFileUrl('');
      setIacFileName('');
      setFormData(prev => ({ ...prev, iacFile: null }));
    } else if (field === 'coolingOffFile') {
      setCoolingOffFileUrl('');
      setCoolingOffFileName('');
      setFormData(prev => ({ ...prev, coolingOffFile: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.acType === '0' || formData.fromDate === '0' || formData.toDate === '0') {
      showSnackbar('Please select AC Type, From Date and To Date', 'error');
      return;
    }

    if (showAcDetails) {
      if (!formData.certificateNo || !formData.issueDate || !formData.validityTill) {
        showSnackbar('Please fill Certificate No, Issue Date and Validity Till', 'error');
        return;
      }

      if (formData.coolingOffApplicable === '1') {
        if (!formData.coolingOffDate) {
          showSnackbar('Please select cooling off period date', 'error');
          return;
        }
      }
    }

    try {
      setLoading(true);

      const submitData = new FormData();
      submitData.append('appId', selectedAppId);
      submitData.append('acType', formData.acType);
      submitData.append('fromDate', formData.fromDate);
      submitData.append('toDate', formData.toDate);
      submitData.append('certificateNo', formData.certificateNo);
      submitData.append('issueDate', formData.issueDate);
      submitData.append('validityTill', formData.validityTill);
      submitData.append('amendmentDate', formData.amendmentDate);
      submitData.append('coolingOffApplicable', formData.coolingOffApplicable);
      submitData.append('coolingOffDate', formData.coolingOffDate);

      let response;
      if (recordId) {
        submitData.append('id', recordId);
        response = await acProcessService.updateACProcessExtended({
          appid: selectedAppId,
          id: recordId,
          acType: formData.acType,
          acDate: formData.fromDate,
          acDateTo: formData.toDate,
          certificateNo: formData.certificateNo,
          issueDate: formData.issueDate,
          validityTill: formData.validityTill,
          amendmentDate: formData.amendmentDate,
          coolingOffApplicable: formData.coolingOffApplicable,
          coolingOffDate: formData.coolingOffDate,
          iacFile: iacFileName,
          coolingOffFile: coolingOffFileName,
          updatedBy: localStorage.getItem('orgId'),
        });
      } else {
        response = await acProcessService.insertACProcess({
          appid: selectedAppId,
          acType: formData.acType,
          acDate: formData.fromDate,
          acDateTo: formData.toDate,
          certificateNo: formData.certificateNo,
          issueDate: formData.issueDate,
          validityTill: formData.validityTill,
          amendmentDate: formData.amendmentDate,
          coolingOffApplicable: formData.coolingOffApplicable,
          coolingOffDate: formData.coolingOffDate,
          iacFile: iacFileName,
          coolingOffFile: coolingOffFileName,
          insertedBy: localStorage.getItem('orgId'),
        });
      }
      
      // After successful insert/update, call Mode 72 to auto-fill MOM file
      let momSuccess = false;
      try {
        await acProcessService.autoFillMOMFile({
          appid: selectedAppId,
          id: recordId,
          acDate: formData.fromDate,
          acDateTo: formData.toDate,
        });
        momSuccess = true;
      } catch (momErr) {
        console.error('Auto-fill MOM error:', momErr);
        momSuccess = false;
      }

      // Stage update logic based on MOM auto-fill result
      const completedStagesForSuccess = ['7', '7a', '7b', '7c', '7d', '7e', '7f', '7g', '12', '12a', '12b', '13', '13a', '13d'];
      const completedStagesForFailure = ['7', '7a', '7b', '7c', '7d', '7e', '7f', '12', '12a', '12b', '13', '13a', '13d'];

      if (momSuccess) {
        // If MOM auto-fill successful
        if (!completedStagesForSuccess.includes(stage)) {
          // Update stage to 6e if not in completed stages
          await acProcessService.updateApplicationStage(selectedAppId, '6e');
        }
      } else {
        // If MOM auto-fill failed
        if (!completedStagesForFailure.includes(stage)) {
          // Update stage to 6d if not in completed stages
          await acProcessService.updateApplicationStage(selectedAppId, '6d');
        }
      }

      // Reload data first
      await loadExistingACData(selectedAppId, selectedOrgId);
      
      // Then show success message
      showSnackbar(recordId?'Data updated successfully':'Data submitted successfully', 'success');
    } catch (err) {
      showSnackbar('Failed to submit data', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm('Are you sure you want to rollback the AC decision?')) {
      return;
    }

    if (!recordId) {
      showSnackbar('No AC record found to rollback', 'error');
      return;
    }

    try {
      setLoading(true);

      await acProcessService.resetACProcess(
        selectedAppId,
        recordId,
        localStorage.getItem('orgId')
      );
      
      showSnackbar('Rollbacked successfully', 'success');
      
      // Reset and reload
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      showSnackbar('Failed to rollback decision', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      labName: '',
      labId: '',
      dealingOfficer: '',
      acType: '0',
      fromDate: '0',
      toDate: '0',
      certificateNo: '',
      issueDate: '',
      validityTill: '',
      amendmentDate: '',
      coolingOffApplicable: '',
      coolingOffDate: '',
      iacFile: null,
      coolingOffFile: null,
    });
    setAcDates([]);
    setAcDatesTo([]);
    setIacFileUrl('');
    setCoolingOffFileUrl('');
    setIacFileName('');
    setCoolingOffFileName('');
    setRecordId(null);
    setShowAcDetails(false);
    setShowRollback(false);
  };

  const acTypeOptions = [
    { value: '0', label: '-----Select-----' },
    { value: 'SEC', label: 'SEC' },
    { value: 'Core', label: 'Core' },
    { value: 'CAT', label: 'CAT' },
    { value: 'CAM', label: 'CAM' },
    { value: 'CAC', label: 'CAC' },
    { value: 'SAC', label: 'SAC' },
    { value: 'CAT (Integrated)', label: 'CAT (Integrated)' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
    { value: 'REVIEW COMMITTEE', label: 'REVIEW COMMITTEE' },
  ];

  // Check if adverse decision based on AC_Status (not stage)
  const isAdverseDecision = ['Denial', 'Force Withdrwal', 'Close', 'Debar from Reapply Certification Preparation'].includes(acStatus);

  const handleFileOpen = async (fileName) => {
      if (!fileName) {
        showSnackbar('No file available to view', 'error');
        return;
      }
  
      try {
        setLoading(true);        
        // Use s3Utils to get signed URL and open file
        await s3Utils.handleFileViewWithS3(fileName, {
          showError: (msg) => showSnackbar(msg, 'error'),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false),
          expiresIn: 172800 // 48 hours
        });
      } catch (err) {
        showSnackbar('Failed to open file', 'error');
        console.error('Error opening file:', err);
      } finally {
        setLoading(false);
      }
    };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Agenda Date Management
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage AC agenda dates and certificate details
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/main/convener/dashboard")}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Search Section */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <SearchIcon sx={{ fontSize: 32, color: "primary.main" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Search Applications
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="User Name"
                placeholder="Enter UserName"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{ height: 40 }}
              >
                {loading ? <CircularProgress size={24} /> : "Search"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Application Dropdown */}
      {applications.length > 0 && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <AssignmentIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Select Application
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>All Applications</InputLabel>
                  <Select
                    value={selectedAppId}
                    label="All Applications"
                    onChange={(e) => handleApplicationChange(e.target.value)}
                  >
                    <MenuItem value="0">-----------Select----------</MenuItem>
                    {applications.map((app) => (
                      <MenuItem
                        key={app.appId || app.AppId}
                        value={app.appId || app.AppId}
                      >
                        {app.name || app.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Details Form */}
      {showDetails && selectedAppId !== "0" && (
        <Box component="form" onSubmit={handleSubmit}>
          <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <CalendarIcon sx={{ fontSize: 40, color: "primary.main" }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Application Details
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {/* Laboratory Details */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Laboratory Name"
                    value={formData.labName}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Lab ID"
                    value={formData.labId}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dealing Officer"
                    value={formData.dealingOfficer}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                {/* AC Details */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>AC Type</InputLabel>
                    <Select
                      value={formData.acType}
                      label="AC Type"
                      onChange={(e) => handleACTypeChange(e.target.value)}
                    >
                      {acTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>From Date</InputLabel>
                    <Select
                      value={formData.fromDate}
                      label="From Date"
                      onChange={(e) => handleFromDateChange(e.target.value)}
                      disabled={formData.acType === "0"}
                    >
                      <MenuItem value="0">-- Select Date --</MenuItem>
                      {acDates.map((date) => (
                        <MenuItem key={date.Id} value={date.Id}>
                          {date.Date}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>To Date</InputLabel>
                    <Select
                      value={formData.toDate}
                      label="To Date"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          toDate: e.target.value,
                        }))
                      }
                      disabled={formData.fromDate === "0"}
                    >
                      <MenuItem value="0">-- Select Date --</MenuItem>
                      {acDatesTo.map((date) => (
                        <MenuItem key={date.Id} value={date.Id}>
                          {date.DateTo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* AC Process Updates Section */}
                {showAcDetails && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>
                        <Chip label="Certificate Details" color="primary" />
                      </Divider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Certificate No."
                        required
                        disabled={true}
                        value={formData.certificateNo}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            certificateNo: e.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Issue Date"
                        type="date"
                        required
                        value={formData.issueDate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            issueDate: e.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Validity Till"
                        type="date"
                        required
                        value={formData.validityTill}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            validityTill: e.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Amendment Date"
                        type="date"
                        value={formData.amendmentDate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            amendmentDate: e.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          Upload IAC minutes (If Applicable) *
                        </Typography>
                        {iacFileName ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {iacFileName}
                            </Typography>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleFileOpen(iacFileName)}
                              disabled={loading}
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveFile("iacFile")}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            type="file"
                            inputProps={{ accept: ".pdf" }}
                            onChange={(e) =>
                              handleFileChange("iacFile", e.target.files[0])
                            }
                            helperText="* Only PDF format allowed"
                          />
                        )}
                      </Box>
                    </Grid>

                    {/* Cooling Off Period for Adverse Decisions */}
                    {isAdverseDecision && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }}>
                            <Chip label="Cooling Off Period" color="warning" />
                          </Divider>
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">
                              Whether Cooling off Period is applicable?
                            </FormLabel>
                            <RadioGroup
                              row
                              value={formData.coolingOffApplicable}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  coolingOffApplicable: e.target.value,
                                }))
                              }
                            >
                              <FormControlLabel
                                value="1"
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                value="0"
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        {formData.coolingOffApplicable === "1" && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Cooling off duration (CAB can apply afresh on or after)"
                                type="date"
                                required
                                value={formData.coolingOffDate}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    coolingOffDate: e.target.value,
                                  }))
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Box>
                                <Typography variant="body2" gutterBottom>
                                  Upload File
                                </Typography>
                                {coolingOffFileName ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {coolingOffFileName}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleFileOpen(coolingOffFileName)}
                                      disabled={loading}
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleRemoveFile("coolingOffFile")
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="file"
                                    onChange={(e) =>
                                      handleFileChange(
                                        "coolingOffFile",
                                        e.target.files[0],
                                      )
                                    }
                                  />
                                )}
                              </Box>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : recordId ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </Button>

                    {showRollback && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleRollback}
                        disabled={loading}
                      >
                        RollBack Process
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default AgendaDate;
